import { useMemo, useState } from 'react';
import { useDataStore } from '@/store/dataStore';
import { TEXAS_COUNTIES, TEXAS_CITIES } from '@/geo/texasCounties';
import type { ReportFilters, ReportFormat } from '@/types/report';
import { isEmptyFilters } from '@/types/report';
import { filterGeographiesForReport } from '@/reports/filterReport';
import { exportReportPdf } from '@/export/reportPdf';
import { exportReportExcel } from '@/export/reportExcel';
import { exportReportWord } from '@/export/reportWord';

type GenerateState = { status: 'idle' | 'loading' | 'success' | 'error'; message?: string };

const FORMAT_OPTIONS: { id: ReportFormat; label: string }[] = [
  { id: 'pdf', label: 'PDF' },
  { id: 'excel', label: 'Excel' },
  { id: 'word', label: 'Word' },
];

/**
 * Flexible Report Builder: county/city/date-range/price-range filters that
 * can be set in any order (they're just independent form fields, not a
 * wizard), plus a multi-select of export formats generated from the same
 * filtered result set. Runs entirely client-side against the same
 * ScoredGeography[] the map renders, so a report can never drift from what's
 * on screen -- see README.md "Report Builder" for why this is a deliberate
 * deviation from a server-rendered pipeline.
 */
export function ReportBuilder() {
  const geographies = useDataStore((s) => s.geographies);
  const sortedCounties = useMemo(() => [...TEXAS_COUNTIES].sort((a, b) => a.name.localeCompare(b.name)), []);
  const sortedCities = useMemo(() => [...TEXAS_CITIES].sort((a, b) => a.name.localeCompare(b.name)), []);

  const [filters, setFilters] = useState<ReportFilters>({});
  const [formats, setFormats] = useState<Set<ReportFormat>>(new Set(['pdf']));
  const [state, setState] = useState<GenerateState>({ status: 'idle' });

  const matched = useMemo(() => filterGeographiesForReport(geographies, filters), [geographies, filters]);

  const setField = <K extends keyof ReportFilters>(key: K, value: ReportFilters[K]) => {
    setState({ status: 'idle' });
    setFilters((prev) => ({ ...prev, [key]: value || undefined }));
  };

  const toggleFormat = (id: ReportFormat) => {
    setFormats((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleReset = () => {
    setFilters({});
    setState({ status: 'idle' });
  };

  const handleGenerate = async () => {
    if (formats.size === 0) {
      setState({ status: 'error', message: 'Choose at least one export format.' });
      return;
    }
    if (matched.length === 0) {
      setState({ status: 'error', message: 'No counties match these filters -- widen the price range or date range and try again.' });
      return;
    }

    setState({ status: 'loading' });
    try {
      if (formats.has('pdf')) exportReportPdf(matched, filters);
      if (formats.has('excel')) exportReportExcel(matched, filters);
      if (formats.has('word')) await exportReportWord(matched, filters);
      setState({ status: 'success', message: `Generated ${formats.size} file${formats.size > 1 ? 's' : ''} for ${matched.length} ${matched.length === 1 ? 'county' : 'counties'}.` });
    } catch (err) {
      setState({ status: 'error', message: (err as Error).message ?? 'Report generation failed unexpectedly.' });
    }
  };

  return (
    <div className="report-builder">
      <div className="report-builder__intro">
        <h2>Report Builder</h2>
        <p>
          Set any combination of filters, in any order, then export. All formats you select are generated from the
          same filtered county list, so they always agree with each other.
        </p>
      </div>

      <div className="report-builder__filters">
        <div className="report-builder__field">
          <label htmlFor="rb-county">County</label>
          <select
            id="rb-county"
            value={filters.countyGeoId ?? ''}
            onChange={(e) => setField('countyGeoId', e.target.value)}
          >
            <option value="">Any county</option>
            {sortedCounties.map((c) => (
              <option key={c.geoId} value={c.geoId}>{c.name}</option>
            ))}
          </select>
        </div>

        <div className="report-builder__field">
          <label htmlFor="rb-city">City</label>
          <select
            id="rb-city"
            value={filters.cityGeoId ?? ''}
            onChange={(e) => setField('cityGeoId', e.target.value)}
            disabled={!!filters.countyGeoId}
          >
            <option value="">Any city</option>
            {sortedCities.map((c) => (
              <option key={c.geoId} value={c.geoId}>{c.name}</option>
            ))}
          </select>
          {filters.countyGeoId && <p className="report-builder__hint">Clear County to filter by city instead.</p>}
          {!filters.countyGeoId && filters.cityGeoId && (
            <p className="report-builder__hint">Reports on {filters.cityGeoId}'s home county -- no separate city-level dataset exists yet.</p>
          )}
        </div>

        <div className="report-builder__field">
          <label>Date range</label>
          <div className="report-builder__row">
            <input
              type="date"
              aria-label="Start date"
              value={filters.dateStart ?? ''}
              onChange={(e) => setField('dateStart', e.target.value)}
            />
            <span>to</span>
            <input
              type="date"
              aria-label="End date"
              value={filters.dateEnd ?? ''}
              onChange={(e) => setField('dateEnd', e.target.value)}
            />
          </div>
        </div>

        <div className="report-builder__field">
          <label>Price range</label>
          <div className="report-builder__row">
            <input
              type="number"
              min={0}
              step={5000}
              placeholder="Min $"
              aria-label="Minimum price"
              value={filters.priceMin ?? ''}
              onChange={(e) => setField('priceMin', e.target.value ? Number(e.target.value) : undefined)}
            />
            <span>to</span>
            <input
              type="number"
              min={0}
              step={5000}
              placeholder="Max $"
              aria-label="Maximum price"
              value={filters.priceMax ?? ''}
              onChange={(e) => setField('priceMax', e.target.value ? Number(e.target.value) : undefined)}
            />
          </div>
        </div>
      </div>

      <div className="report-builder__formats">
        <span className="report-builder__formats-label">Export as</span>
        {FORMAT_OPTIONS.map((f) => (
          <label key={f.id} className="report-builder__format-toggle">
            <input type="checkbox" checked={formats.has(f.id)} onChange={() => toggleFormat(f.id)} />
            {f.label}
          </label>
        ))}
      </div>

      <div className="report-builder__preview">
        {isEmptyFilters(filters)
          ? `Statewide: all ${geographies.length} counties will be included.`
          : `${matched.length} of ${geographies.length} counties match your filters.`}
      </div>

      <div className="report-builder__actions">
        <button type="button" className="report-builder__generate" onClick={handleGenerate} disabled={state.status === 'loading'}>
          {state.status === 'loading' ? 'Generating…' : 'Generate Report'}
        </button>
        <button type="button" className="report-builder__reset" onClick={handleReset}>
          Reset filters
        </button>
      </div>

      {state.status === 'success' && <p className="report-builder__status report-builder__status--success">{state.message}</p>}
      {state.status === 'error' && <p className="report-builder__status report-builder__status--error">{state.message}</p>}
    </div>
  );
}
